// A floating button for the guest's Wolt delivery, on every page, and the
// panel it opens: how far the delivery has got, when it should arrive, where
// it is going, Wolt's tracking page and the venue's number. A failed delivery
// turns the button red and stays until the guest taps "Got it". On the menu
// page the panel takes over from ProcessingOrderModal as the venue accepts.
//
// The web version of the app's DeliveryFab + DeliveryTrackingModal. The rules
// live in utils/deliveryTracking.js, the data in hooks/useActiveDeliveries.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useDataContext } from '../../context/data';
import { Modal } from '../ui/Modal';
import translations from '../../constants/translations.json';
import { useActiveDeliveries } from '../../hooks/useActiveDeliveries';
import {
  FINAL_STAGE_INDEX,
  deliveryFailureTextKey,
  deliveryPillLabelKey,
  deliveryStage,
  hasDeliveryFailed,
  minutesUntil,
  stageFill,
} from '../../utils/deliveryTracking';

// Pages the button stays off: the kiosk is not a guest's own device, and the
// staff door scanner is not a guest page.
const HIDDEN_PAGES = /^\/(restaurant-kiosk|staff)(\/|$)/;
// Pages with the basket bar pinned to the bottom: the button sits above it.
// The choose-order step has no bar, so there it stays at the bottom.
const BASKET_BAR_PAGES = /^\/(restaurants|restaurant-together)\/[^/]+/;
const NO_BAR_STEP = /\/choose-order\/?$/;

const RING_RADIUS = 15;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ fill, failed }) => (
  <span className="relative flex items-center justify-center w-9 h-9 shrink-0">
    <svg width="36" height="36" viewBox="0 0 36 36" className="absolute inset-0 -rotate-90">
      <circle
        cx="18"
        cy="18"
        r={RING_RADIUS}
        fill="none"
        stroke="rgba(0,0,0,0.18)"
        strokeWidth="3"
      />
      <circle
        cx="18"
        cy="18"
        r={RING_RADIUS}
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - fill / 100)}
      />
    </svg>
    <i
      className={`fas ${failed ? 'fa-exclamation-triangle' : 'fa-motorcycle'} text-[13px]`}
      aria-hidden="true"
    />
  </span>
);

// One stage per delivery. Remembering the last step shown per order is what
// lets a status this build does not know hold its place instead of snapping
// back to "preparing" — keyed per order so two deliveries never mix.
const useStages = (deliveries) => {
  const lastIndex = useRef({});
  return useMemo(() => {
    const stages = {};
    deliveries.forEach((d) => {
      const stage = deliveryStage(d.status, d.cancelledAt, lastIndex.current[d.orderId] ?? 0);
      if (stage.index !== null) lastIndex.current[d.orderId] = stage.index;
      stages[d.orderId] = stage;
    });
    return stages;
  }, [deliveries]);
};

const DeliveryTracker = () => {
  const { userProfile, language } = useDataContext();
  const { pathname } = useLocation();
  const hidden = HIDDEN_PAGES.test(pathname);

  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { deliveries, dismiss, refresh, handoff } = useActiveDeliveries(
    userProfile?.token || null,
    { panelOpen, enabled: !hidden },
  );
  const stages = useStages(deliveries);

  // Handed over by the menu page as ProcessingOrderModal made way: open on that
  // delivery, once — never later, should it only show up afterwards.
  const openedHandoff = useRef(0);
  useEffect(() => {
    if (!handoff || handoff.seq === openedHandoff.current) return;
    openedHandoff.current = handoff.seq;
    if (!deliveries.some((d) => d.orderId === handoff.orderId)) return;
    setSelectedId(handoff.orderId);
    setPanelOpen(true);
  }, [handoff, deliveries]);

  const t = useCallback(
    (key) => translations[language]?.[key] || translations.en[key] || '',
    [language],
  );

  // The countdown is worked out from a fixed ETA, so without a tick it would
  // read "in 25 min" for the whole delivery.
  const [, setMinuteTick] = useState(0);
  const hasEta = deliveries.some((d) => d.dropoffETA);
  useEffect(() => {
    if (!hasEta) return undefined;
    const interval = setInterval(() => setMinuteTick((n) => n + 1), 60000);
    return () => clearInterval(interval);
  }, [hasEta]);

  // Newest first: the button describes the latest delivery, the panel lists all.
  const primary = deliveries[0] || null;
  const selected = deliveries.find((d) => d.orderId === selectedId) || primary;

  // Nothing left to show (delivered, dismissed, signed out): the panel closes
  // rather than springing open by itself when a later delivery arrives.
  useEffect(() => {
    if (!primary && panelOpen) setPanelOpen(false);
  }, [primary, panelOpen]);

  // The shared Modal clears the page's scroll lock whenever any modal closes —
  // the accept notice shown over this panel as it opens, say — so while the
  // panel is up the lock is put back.
  const panelShown = panelOpen && !hidden && Boolean(primary);
  useEffect(() => {
    if (!panelShown) return undefined;
    const relock = new MutationObserver(() => {
      if (document.body.style.overflow !== 'hidden') document.body.style.overflow = 'hidden';
    });
    relock.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => relock.disconnect();
  }, [panelShown]);

  const openPanel = () => {
    setSelectedId(null);
    setPanelOpen(true);
    // the latest word on it, not the one from up to half a minute ago
    if (primary) refresh(primary.orderId);
  };
  const closePanel = useCallback(() => setPanelOpen(false), []);

  if (hidden || !primary) return null;

  const primaryStage = stages[primary.orderId];
  const primaryFailed = hasDeliveryFailed(primary);
  const primaryMinutes = primaryFailed ? null : minutesUntil(primary.dropoffETA);
  const extraCount = deliveries.length - 1;

  const stage = selected ? stages[selected.orderId] : null;
  const selectedFailed = selected ? hasDeliveryFailed(selected) : false;
  const selectedMinutes = selected ? minutesUntil(selected.dropoffETA) : null;
  const eta =
    selectedMinutes === null
      ? t('delivery_eta_unknown')
      : t('delivery_eta_minutes').replace('{minutes}', String(selectedMinutes));
  const lifted = BASKET_BAR_PAGES.test(pathname) && !NO_BAR_STEP.test(pathname);

  return (
    <>
      <div
        className={`fixed right-4 sm:right-6 z-[95] ${
          lifted ? 'bottom-[164px] sm:bottom-[104px]' : 'bottom-6'
        }`}
      >
        <button
          type="button"
          onClick={openPanel}
          aria-label={t('delivery_fab_label')}
          className={`flex items-center gap-2.5 h-[52px] pl-2 pr-5 rounded-full text-white border-none cursor-pointer shadow-[0_3px_10px_rgba(0,0,0,0.18)] ${
            primaryFailed ? 'bg-[#ef4444] hover:bg-[#dc2626]' : 'bg-primary hover:bg-secondary'
          }`}
        >
          <ProgressRing fill={stageFill(primaryStage)} failed={primaryFailed} />
          <span className="flex flex-col items-start text-left leading-tight max-w-[210px] sm:max-w-[280px]">
            <span className="text-sm font-bold truncate max-w-[210px] sm:max-w-[280px]">
              {t(deliveryPillLabelKey(primary, primaryStage))}
            </span>
            {primaryMinutes !== null && (
              <span className="text-xs">
                {t('delivery_eta_minutes').replace('{minutes}', String(primaryMinutes))}
              </span>
            )}
          </span>
        </button>
        {/* More than one delivery: the button shows the newest, the badge how
            many more the panel lists. */}
        {extraCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-[#1a1a1a] text-xs font-bold flex items-center justify-center shadow">
            +{extraCount}
          </span>
        )}
      </div>

      {/* Mounted only while open: the shared Modal clears the page's scroll
          lock whenever an instance re-renders, and this one re-renders every
          minute — it must not unlock the page under another open modal. */}
      {panelOpen && selected && stage && (
        <Modal isOpen onClose={closePanel} contentClassName="max-w-[440px]">
          <div className="pt-8 px-6 pb-6">
            <h3 className="text-xl font-bold text-[#1a1a1a] mb-1 pr-8">
              {t('delivery_tracking_title')}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {selected.restaurantName} ·{' '}
              <span className="whitespace-nowrap">
                {t('restaurant_menu_order_id_label')}: #{selected.refNo}
              </span>
            </p>

            {deliveries.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {deliveries.map((d) => (
                  <button
                    key={d.orderId}
                    type="button"
                    onClick={() => {
                      setSelectedId(d.orderId);
                      refresh(d.orderId);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border cursor-pointer ${
                      d.orderId === selected.orderId
                        ? 'bg-primary text-white border-transparent'
                        : 'bg-white text-[#555] border-gray-200'
                    }`}
                  >
                    {d.restaurantName}
                  </button>
                ))}
              </div>
            )}

            {/* A half-full bar beside "we couldn't complete this delivery" reads
                worse than none, so a failure gets its explanation instead. */}
            {selectedFailed ? (
              <div className="flex items-start gap-2.5 rounded-xl bg-red-50 p-3.5 mb-5">
                <i className="fas fa-exclamation-circle text-red-600 mt-0.5" aria-hidden="true" />
                <p className="text-sm font-semibold text-red-600 m-0">
                  {t(deliveryFailureTextKey(selected, stage))}
                </p>
              </div>
            ) : (
              <div className="mb-5">
                <div className="flex gap-1.5 mb-3" aria-hidden="true">
                  {Array.from({ length: FINAL_STAGE_INDEX + 1 }).map((_, index) => (
                    <span
                      key={index}
                      className={`h-1.5 flex-1 rounded-full ${
                        index <= (stage.index ?? 0) ? 'bg-primary' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-base font-bold text-[#1a1a1a] mb-0.5">{t(stage.labelKey)}</p>
                <p className="text-sm text-gray-500 m-0">
                  {t('delivery_eta_label')}: {eta}
                </p>
              </div>
            )}

            {/* A scheduled delivery: when it was ordered for, as the order
                confirmation said it before the panel took over. */}
            {selected.scheduledFor && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-0.5">{t('delivery_time')}</p>
                <p className="text-sm text-[#1a1a1a] m-0">
                  {moment(selected.scheduledFor).format('DD/MM/YYYY HH:mm')}
                </p>
              </div>
            )}

            {selected.dropoffAddress && (
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-0.5">{t('delivery_dropoff_label')}</p>
                <p className="text-sm text-[#1a1a1a] m-0">{selected.dropoffAddress}</p>
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              {selected.trackingLink && !selectedFailed && (
                <a
                  href={selected.trackingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-[14px] rounded-xl bg-primary hover:bg-secondary text-white text-[15px] font-semibold text-center no-underline"
                >
                  <i className="fas fa-map-marker-alt mr-2" aria-hidden="true" />
                  {t('orders_track_delivery')}
                </a>
              )}
              {selected.restaurantPhone && (
                <a
                  href={`tel:${selected.restaurantPhone}`}
                  className="w-full py-[14px] rounded-xl border border-primary text-primary text-[15px] font-semibold text-center no-underline"
                >
                  <i className="fas fa-phone rotate-90 mr-2" aria-hidden="true" />
                  {t('delivery_contact_restaurant')}
                </a>
              )}
              {/* Only a failure is dismissed: a running delivery retires itself. */}
              {selectedFailed && (
                <button
                  type="button"
                  onClick={() => dismiss(selected.orderId)}
                  className="w-full py-[14px] rounded-xl bg-[#1a1a1a] hover:bg-[#333] text-white text-[15px] font-semibold border-none cursor-pointer"
                >
                  {t('delivery_dismiss')}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default DeliveryTracker;
